/**
 * Append the display-resize tier (`width`/`quality`) to a v1 content URL.
 *
 * The tier policy lives at the call site's surface level (thumbnail vs
 * full display); print and download paths must NOT use this — originals
 * only. Non-v1 URLs pass through untouched and an already-sized URL is
 * returned as-is (idempotent).
 */
export function contentImageUrlWithSize(
  url: string,
  width: number,
  quality?: number
): string {
  if (!(url && url.includes('/api/v1/content/')) || /[?&]width=/.test(url)) {
    return url;
  }
  const joiner = url.includes('?') ? '&' : '?';
  const qualitySuffix = quality === undefined ? '' : `&quality=${quality}`;
  return `${url}${joiner}width=${width}${qualitySuffix}`;
}

/**
 * Inverse of `contentImageUrlWithSize`: drop the display-resize tier so
 * download/print paths can recover the archival original from a URL that
 * already went through display sizing. Other params (`variant`) survive.
 */
export function contentImageUrlWithoutSize(url: string): string {
  if (!(url?.includes('/api/v1/content/') && url.includes('?'))) {
    return url;
  }
  const splitAt = url.indexOf('?');
  const path = url.slice(0, splitAt);
  const params = url
    .slice(splitAt + 1)
    .split('&')
    .filter((param) => !/^(width|quality)=/.test(param));
  return params.length ? `${path}?${params.join('&')}` : path;
}
